import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, Banknote, MessageSquare, Server, MapPin, Scale, ArrowRight
} from 'lucide-react';
import usePrices from './hooks/usePrices';
import { SITE, formatMad } from './data/servers';
import { PAGES, GAME_META, gamePath, serverPath, gameLabel } from './site/routes';
import { FAQ } from './site/content';
import { fetchArticles } from './site/articlesApi';
import ArticleCard from './site/ArticleCard';
import {
  usePageSeo, Section, PriceTicker, TrustGrid, LinkCards, FaqList, CtaBand, ArrowLink, byGame
} from './site/ui';

const Hero = ({ servers }) => (
  <section className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-950 to-black">
    <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_40%,rgba(250,204,21,0.18),transparent_50%)]" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-14 lg:py-20 text-center">
      <p className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-yellow-500/10 border border-yellow-500/30 rounded-full mb-4 text-yellow-400 text-xs sm:text-sm font-medium">
        <MapPin size={14} className="flex-shrink-0" /> Achat et vente de Kamas au Maroc · paiement en DH
      </p>
      <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
        <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">Achat Kamas</span>
        {' & '}
        <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">Vente Kamas</span>
        <br />
        <span className="text-white">Dofus au Maroc</span>
      </h1>
      <p className="text-sm sm:text-lg text-gray-400 mb-6 max-w-3xl mx-auto leading-relaxed">
        AV Kamas est la plateforme marocaine pour <Link to={PAGES.buyGuide} className="text-gray-200 font-semibold hover:text-yellow-400">acheter des Kamas</Link> et{' '}
        <Link to={PAGES.sellGuide} className="text-gray-200 font-semibold hover:text-yellow-400">vendre vos Kamas</Link> sur{' '}
        <Link to={gamePath('DOFUS 3')} className="hover:text-yellow-400">Dofus 3</Link>,{' '}
        <Link to={gamePath('DOFUS RETRO')} className="hover:text-yellow-400">Dofus Rétro</Link> et{' '}
        <Link to={gamePath('DOFUS TOUCH')} className="hover:text-yellow-400">Dofus Touch</Link>.
        Prix mis à jour en continu, moins chers que TryAndJudge à l'achat et mieux payés à la vente (jusqu'à 1 DH par million),
        paiement par virement bancaire marocain.
      </p>
      <div className="grid grid-cols-2 sm:flex sm:justify-center gap-3 sm:gap-4">
        <Link to={PAGES.buy} className="px-3 sm:px-8 py-3 sm:py-4 text-sm sm:text-base bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-black font-bold rounded-xl flex items-center justify-center gap-2 transition-all">
          <TrendingUp size={18} className="flex-shrink-0" /> Acheter des Kamas
        </Link>
        <Link to={PAGES.sell} className="px-3 sm:px-8 py-3 sm:py-4 text-sm sm:text-base bg-gray-800 hover:bg-gray-700 text-white font-bold rounded-xl border border-gray-700 flex items-center justify-center gap-2 transition-all">
          <TrendingDown size={18} className="flex-shrink-0" /> Vendre des Kamas
        </Link>
      </div>
      <ul className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mt-6 sm:mt-10 text-left">
        {[
          { icon: Server, title: `${servers.length} serveurs officiels`, text: 'Dofus 3, Rétro et Touch', to: PAGES.servers },
          { icon: Scale, title: 'Mieux que TryAndJudge', text: "Jusqu'à 1 DH/M, achat et vente", to: PAGES.comparison },
          { icon: Banknote, title: 'Paiement en DH', text: 'Virement banques marocaines', to: PAGES.dofusMaroc },
          { icon: MessageSquare, title: 'Suivi en direct', text: 'Chat et preuve de paiement', to: PAGES.faq },
        ].map(({ icon: Icon, title, text, to }) => (
          <li key={title}>
            <Link to={to} className="h-full bg-gray-900/60 border border-gray-800 hover:border-yellow-500/40 rounded-xl p-3 sm:p-5 flex sm:block items-start gap-2 transition-colors">
              <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400 sm:mb-2 flex-shrink-0 mt-0.5 sm:mt-0" />
              <span>
                <span className="block font-semibold text-white text-sm sm:text-base leading-snug">{title}</span>
                <span className="block text-xs sm:text-sm text-gray-400">{text}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

// Un bloc par jeu : prix de départ + lien vers la page du jeu
const GameCards = ({ servers }) => (
  <ul className="grid md:grid-cols-3 gap-3 sm:gap-4">
    {byGame(servers).map(({ game, servers: list }) => {
      const cheapest = Math.min(...list.map(s => s.buy));
      return (
        <li key={game}>
          <Link to={gamePath(game)} className="group h-full flex flex-col bg-gray-900/80 border border-gray-800 hover:border-yellow-500/50 rounded-2xl p-4 sm:p-5 transition-colors">
            <span className="flex items-center justify-between gap-2">
              <span className="text-lg font-bold text-white group-hover:text-yellow-400">Kamas {gameLabel(game)}</span>
              <ArrowRight size={18} className="text-gray-500 group-hover:text-yellow-400 flex-shrink-0" />
            </span>
            <span className="text-xs sm:text-sm text-gray-400 mt-1 mb-3 leading-relaxed">{GAME_META[game].intro}</span>
            <span className="mt-auto flex items-center justify-between text-sm">
              <span className="text-gray-500">{list.length} serveurs</span>
              <span className="text-green-400 font-semibold">dès {formatMad(cheapest)}</span>
            </span>
          </Link>
        </li>
      );
    })}
  </ul>
);

// Tous les serveurs en liens compacts vers leur page dédiée
const ServerLinks = ({ servers }) => (
  <div className="space-y-4">
    {byGame(servers).map(({ game, servers: list }) => (
      <div key={game}>
        <h3 className="text-sm sm:text-base font-semibold text-gray-300 mb-2">
          <Link to={gamePath(game)} className="hover:text-yellow-400">Kamas {gameLabel(game)}</Link>
        </h3>
        <ul className="flex flex-wrap gap-2">
          {list.map(s => (
            <li key={s.slug}>
              <Link to={serverPath(s)} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-900 border border-gray-800 hover:border-yellow-500/50 text-xs sm:text-sm">
                <span className="text-white font-medium">Kamas {s.name}</span>
                <span className="text-green-400">{formatMad(s.buy)}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

export default function LandingPage() {
  const { servers } = usePrices();
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    fetchArticles().then(list => setArticles(list.slice(0, 3))).catch(() => {});
  }, []);

  usePageSeo({
    title: 'Achat Kamas Maroc & Vente Kamas Maroc | Dofus, Rétro, Touch – AV Kamas',
    description: "Achat Kamas et vente Kamas au Maroc sur les 20 serveurs Dofus 3, Dofus Rétro et Dofus Touch. Paiement en DH, moins cher que TryAndJudge.",
    path: PAGES.home,
    jsonLd: [{
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Achat et vente de Kamas Dofus au Maroc',
      serviceType: 'Achat Kamas, Vente Kamas',
      provider: { '@type': 'Organization', name: SITE.name, url: SITE.url },
      areaServed: { '@type': 'Country', name: 'Maroc' },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Prix des Kamas par serveur',
        itemListElement: servers.map(s => ({
          '@type': 'Offer',
          name: `1M Kamas ${s.name} (${gameLabel(s.game)})`,
          price: s.buy.toFixed(2),
          priceCurrency: 'MAD',
          url: `${SITE.url}${serverPath(s)}`,
        })),
      },
    }],
  });

  return (
    <main className="bg-gray-950 text-white">
      <PriceTicker servers={servers} />
      <Hero servers={servers} />

      <Section
        id="prix-kamas"
        title={<>Prix des Kamas <span className="text-yellow-400">par jeu</span></>}
        intro="Prix en dirhams pour 1 million de Kamas (1M), sur tous les serveurs officiels. Choisissez votre jeu pour voir le détail serveur par serveur."
      >
        <GameCards servers={servers} />
        <div className="mt-4"><ArrowLink to={PAGES.prices}>Voir tous les prix des Kamas</ArrowLink></div>
      </Section>

      <Section
        id="achat-vente"
        title={<>Achat Kamas Maroc et <span className="text-yellow-400">Vente Kamas Maroc</span></>}
        className="bg-gray-900/40"
      >
        <LinkCards
          columns="sm:grid-cols-2"
          links={[
            { to: PAGES.buyGuide, title: 'Achat Kamas Maroc : acheter des Kamas en dirhams', text: "Payez en dirhams depuis votre banque marocaine et recevez vos Kamas directement sur votre personnage, en 4 étapes." },
            { to: PAGES.sellGuide, title: 'Vente Kamas Maroc : vendez vos Kamas contre des DH', text: "Vendez vos Kamas plus cher que chez TryAndJudge (jusqu'à 1 DH de plus par million), payés par virement sur votre compte bancaire marocain." },
          ]}
        />
      </Section>

      <Section
        id="serveurs-dofus"
        title={<>Tous les serveurs <span className="text-yellow-400">Dofus</span></>}
        intro={`Achat et vente de Kamas sur les ${servers.length} serveurs officiels d'Ankama. Chaque serveur a sa page avec son prix du jour et sa description.`}
      >
        <ServerLinks servers={servers} />
        <div className="mt-4"><ArrowLink to={PAGES.servers}>Découvrir tous les serveurs Dofus</ArrowLink></div>
      </Section>

      <Section
        id="garanties"
        title={<>Achat et vente de Kamas <span className="text-yellow-400">en toute transparence</span></>}
        className="bg-gray-900/40"
      >
        <TrustGrid />
      </Section>

      <Section id="guides" title={<>Guides et <span className="text-yellow-400">comparatifs</span></>}>
        <LinkCards
          links={[
            { to: PAGES.comparison, title: 'AV Kamas vs TryAndJudge', text: 'Comparatif des prix serveur par serveur, mis à jour automatiquement.' },
            { to: PAGES.dofusMaroc, title: 'Dofus Maroc', text: 'Une plateforme pensée pour les joueurs marocains : paiement en DH, support dédié.' },
            { to: PAGES.faq, title: 'Questions fréquentes', text: "Paiement, délais, compte, prix par million : toutes les réponses sur l'achat et la vente de Kamas." },
          ]}
        />
      </Section>

      {articles.length > 0 && (
        <Section id="articles" title={<>Derniers <span className="text-yellow-400">articles</span></>} className="bg-gray-900/40">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {articles.map(article => <ArticleCard key={article.id} article={article} />)}
          </div>
          <div className="mt-4"><ArrowLink to={PAGES.articles}>Tous les articles</ArrowLink></div>
        </Section>
      )}

      <Section
        id="faq"
        title={<>Questions fréquentes sur l'achat et la vente de <span className="text-yellow-400">Kamas</span></>}
      >
        <FaqList items={FAQ.slice(0, 4)} />
        <div className="mt-4"><ArrowLink to={PAGES.faq}>Voir toutes les questions</ArrowLink></div>
      </Section>

      <CtaBand />
    </main>
  );
}
